import type { RetentionMode } from "../models.ts";
import type { FamilyTier } from "./tiers.ts";

/**
 * The curated part of labs and families (openspec change `model-picking`, D3 amended by Q18b):
 * `catalogue:sync` derives the served labs, their Bedrock ID prefixes, their families and each
 * family's default tier from the pinned snapshot (`labs.generated.ts`); only the exceptions and
 * the family rules live here, reviewed with every sync. This module imports nothing generated,
 * so the sync can read it before `labs.generated.ts` exists.
 */

/**
 * Every tenant's default `models.labs` (D4, Q18b): every tenant starts on these labs only,
 * whatever the stage serves; a tenant admin opts in to more. The only place code names a lab
 * as a default; a test pins that each is a served lab no operator may block.
 */
export const INSTANCE_DEFAULT_LABS = ["anthropic"] as const;

/** A lab's curated exceptions: a display name the snapshot lacks (it has no `amazon` provider). */
export interface LabOverride {
  readonly displayName?: string;
}

export const LAB_OVERRIDES: Readonly<Record<string, LabOverride | undefined>> = {
  amazon: { displayName: "Amazon" },
};

/**
 * How a lab's reasoning families reason natively on Bedrock (D15 amended by Q18b), verified
 * against the Bedrock and model documentation, never asserted from memory:
 *
 * - `anthropic`: Anthropic's extended thinking, adaptive from the family's `adaptiveReasoningFrom`
 *   version, else a thinking budget (Bedrock user guide, "Adaptive thinking", Converse
 *   `additionalModelRequestFields.thinking` and `output_config.effort`).
 * - `reasoning-config`: Amazon Nova 2's `additionalModelRequestFields.reasoningConfig`
 *   `{ type: "enabled", maxReasoningEffort: "low" | "medium" | "high" }` (Nova 2 user guide,
 *   "Extended thinking", read 2026-11-02). `high` requires `maxTokens`, temperature and top-p
 *   unset, which ESI's calls always set, so the style accepts `low` and `medium` only.
 * - `none`: no documented Converse field was found (OpenAI GPT 5.6/6, xAI Grok, Moonshot Kimi,
 *   Zhipu GLM: the Bedrock posts read 2026-11-02 document the Responses API's `reasoning`
 *   parameter, not the Converse field), or the lab's models do not reason. Nothing is sent; a
 *   model that reasons by default (Grok) reasons at its own default while ESI records `off`.
 *
 * A served lab with a reasoning-capable family must have an entry (the drift test).
 */
export type LabReasoningStyle = "anthropic" | "reasoning-config" | "none";

export const LAB_REASONING: Readonly<Record<string, LabReasoningStyle | undefined>> = {
  anthropic: "anthropic",
  amazon: "reasoning-config",
  moonshotai: "none",
  openai: "none",
  xai: "none",
  zhipuai: "none",
};

/**
 * A family's curated exceptions and rules (D3): a display name pinned over the derived one, a
 * tier over the derived default, the Bedrock retention mode its models require, the version of
 * its implications (a consent is to one version; bump it when the rules change), and, for an
 * `anthropic`-style lab, the first version that takes adaptive reasoning (older ones take a
 * thinking budget; absent: every version takes a budget).
 */
export interface FamilyOverride {
  readonly displayName?: string;
  readonly tier?: FamilyTier;
  readonly retention?: RetentionMode;
  readonly implicationsVersion?: number;
  readonly adaptiveReasoningFrom?: readonly number[];
}

/**
 * Retention per family is from the Bedrock user guide, "Data retention"
 * (https://docs.aws.amazon.com/bedrock/latest/userguide/data-retention.html, read 2026-10-23):
 * "For models requiring `aws_review` (currently Claude Fable 5 and Claude Fable 5.1)", retained
 * up to 30 days in the processing Region and possibly reviewed by AWS, never shared with the
 * provider; every other family runs under `default`. Adaptive thinking is accepted from Claude
 * 4.6 on (Opus 4.6, Sonnet 4.6 and every later version); Opus 4.5, Sonnet 4.5, Sonnet 4 and
 * Haiku 4.5 take a thinking budget only. `amazon/nova` holds Nova 2 Lite: its derived name
 * ("Nova Lite") would read as the first-generation family's. OpenAI: the snapshot describes
 * Terra as "balanced … everyday work" and Sol as "for complex professional work": Terra is
 * `balanced` and Sol `deep` by override. Since @opencode-ai/models 0.0.119 the price rank agrees
 * (GPT-6.1 Terra, $5 output, below GPT Sol 6.1 at $10); the override keeps the tiers if a
 * later snapshot inverts them again, as 0.0.118 did (GPT-5.6 Terra, $12). It does not keep the
 * key order: catalogue-sync sorts families by the derived tier.
 */
export const FAMILY_OVERRIDES: Readonly<Record<string, FamilyOverride | undefined>> = {
  "anthropic/claude-haiku": { adaptiveReasoningFrom: [4, 6] },
  "anthropic/claude-sonnet": { adaptiveReasoningFrom: [4, 6] },
  "anthropic/claude-opus": { adaptiveReasoningFrom: [4, 6] },
  "anthropic/claude-fable": { retention: "aws_review", adaptiveReasoningFrom: [5] },
  "amazon/nova": { displayName: "Nova 2" },
  "openai/gpt-terra": { tier: "balanced" },
  "openai/gpt-sol": { tier: "deep" },
};
